import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';

import BannerStyleCheckbox, {
  BannerStyleCheckboxProps,
} from '../BannerStyleCheckbox';

import { Subtitle2, Tag } from '../Typography';

import useDebounce from '../../app/hooks/useDebounce';
import {
  toggleCheckbox,
  clearToggleCheckboxError,
} from '../../app/actions/treatmentPage';

import LockerImage from '../../assets/storybook-assets/images/locker-image.png';

import styles from './index.module.scss';

const DEBOUNCE_DELAY = 1000;

export interface BannerStyleCheckboxListProps {
  label: string;
  dayNumber: string;
  items: BannerStyleCheckboxProps[];
  className?: string;
  onClick?: (dayNumber: string) => void;
  treatmentId?: string;
  stepId?: string;
  dispatch?: any;
  error?: string;
  data?: object;
  completed?: boolean;
  listIndex?: number;
  onCheckboxToggle?: (listIndex: number, itemIndex: number) => void;
  inactive?: boolean;
}

const BannerStyleCheckboxList: React.FC<BannerStyleCheckboxListProps> = ({
  label,
  dayNumber,
  items,
  className,
  treatmentId,
  stepId,
  dispatch,
  error,
  data,
  completed,
  listIndex,
  onCheckboxToggle,
  inactive,
}) => {
  const [checkboxErrorIndex, setCheckboxErrorIndex] = useState<number | null>(
    null,
  );

  const language = localStorage.getItem('hy-language') || 'en';

  useEffect(() => {
    if (data && !error) {
      setCheckboxErrorIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error]);

  const { debouncedCallback: debouncedToggleCheckbox } = useDebounce(() => {
    dispatch(toggleCheckbox(stepId, Number(dayNumber), treatmentId, language));
  }, DEBOUNCE_DELAY);

  const handleToggleCheckbox = (itemIndex: number) => {
    dispatch(clearToggleCheckboxError());

    if (onCheckboxToggle && listIndex !== undefined) {
      onCheckboxToggle(listIndex, itemIndex);
    }

    setCheckboxErrorIndex(itemIndex);
    debouncedToggleCheckbox();
  };

  return (
    <div className={cx(styles.wrapper, className)}>
      <div
        className={cx(styles.header, { [styles.headerCompleted]: completed })}
      >
        <Subtitle2 className={styles.label}>
          {label + ' ' + dayNumber}
        </Subtitle2>

        <Tag className={styles.numberOfItems}>{items.length}</Tag>
      </div>

      {items && items.length > 0 && (
        <div className={styles.list}>
          {items.map((item, index) => {
            const checkboxHasError = !!error && checkboxErrorIndex === index;
            return (
              <BannerStyleCheckbox
                key={index}
                className={cx(styles.listItem, {
                  [styles.listItemNotCompleted]: !completed,
                  [styles.listItemNotClickable]: inactive || completed,
                  [styles.error]: checkboxHasError,
                })}
                checkboxContentClassName={cx({
                  [styles.checkboxContentNotCompleted]: !completed,
                  [styles.checkboxContentCompleted]: completed,
                  [styles.checkboxContentInactive]: inactive,
                })}
                onClick={() => handleToggleCheckbox(index)}
                iconRightUrl={inactive || completed ? LockerImage : null}
                {...item}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  error: state.treatmentPage.get('toggleCheckboxError'),
  data: state.treatmentPage.get('data'),
});

export default connect(mapStateToProps)(BannerStyleCheckboxList);
